import { ApprovalData } from "./ApprovalDetailsPage.types";
import { makeCygnusApiCall } from "../../../api/api";
import { GridApiProps } from "../../reusable_components/CygnusGrid/CygnusGrid.utils";

/**
 * Fetches the approval page data from the API
 * @param userName - The user name
 * @param userRole - The user role
 * @returns A Promise resolving to the approval details
 */
export const fetchApprovalPageData = async (
  userName: string,
  userRole: string,
  {
    offset,
    limit,
    searchText,
    sourceCode,
    filter,
  }: GridApiProps
): Promise<{ records: any[]; totalCount: number }> => {
  const page = Math.floor(offset / limit) + 1;
  const params = new URLSearchParams();

  params.set("page", String(page));
  params.set("page_size", String(limit));
  params.set("search_text", String(searchText));
  params.set("ApproverName", encodeURIComponent(userName));
  params.set("ApproverRole", encodeURIComponent(userRole));
  params.set("ApprovalStatus", JSON.stringify(["Pending Approval"]));
  params.set("SendForApproval", "Y");

  // Add ALL filter keys (coming from CygnusGrid's filterKeyMap)
  const append = (key: string, val: any) => {
    if (val == null || val === "") return;
    if (Array.isArray(val)) {
      if (val.length === 0) return;
      params.set(key, JSON.stringify(val.map(v => String(v).trim())));
    } else if (val instanceof Date) {
      params.set(key, val.toISOString());
    } else {
      params.set(key, String(val));
    }
  };

  Object.entries(filter ?? {}).forEach(([k, v]) => append(k, v));

  const url = `/engagement-letter/el_approvals/?${params.toString()}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url: url,
    show_message: true,
    customized_success_message: "Fetched approval data successfully.",
    customized_failure_message: "Failed to fetch approval data.",
    customized_error_message: "Error occurred while fetching approval data.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    const records: any = response.data;
    const totalRecords: number = response.total_records || 0;
    return { records, totalCount: totalRecords };
  } else {
    return { records: [], totalCount: 0 };
  }
};

/**
 * Saves the engagement letter approval details
 * @param selectedRows - Selected rows with approval data
 * @param approvalStatus - The approval status ("Approved" or "Rejected")
 * @param message - Optional message for rejection
 * @returns A Promise resolving to a success boolean
 */
export const saveEngagementLetterApprovalDetails = async (
  selectedRows: ApprovalData[],
  approvalStatus: "Approved" | "Rejected",
  message: string = ""
): Promise<boolean> => {
  if (selectedRows.length === 0) return false;

  const requestBody = selectedRows.map(row => ({
    ...row,
    ApprovalStatus: approvalStatus,
    Message: message,
  }));

  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/engagement-letter/el_approvals/",
    payload: requestBody,
    show_message: true,
    customized_success_message: "Approval details saved successfully.",
    customized_failure_message: "Failed to save approval details.",
    customized_error_message: "Error occurred while saving approval details.",
    isblob: false,
    forMDM: true,
  });

  return response?.success ?? false;
};
